import React, {
  useState,
  useEffect
} from 'react';
import PropTypes from 'prop-types';

import {
  Flex,
  Label,
  Modal,
  Select,
  TextInput,
  useNotification
} from '@sopranodesign/pulse-components';
import { useQueryClient } from '@sopranodesign/pulse-hooks';

import {
  resyncAllZendeskTickets,
  saveZendeskTicketFilter,
  deleteZendeskTicketFilter,
  resyncZendeskTicketFilter,
  recrawlZendeskTicket,
  saveZendeskTicketTags,
  createZendeskTicketsTag,
  fetchZendeskTicketTags,
  fetchZendeskTicketsAvailableTags,
  fetchZendeskTicketAvailableTagValues,
  fetchZendeskTicketAvailableOrganizations,
  fetchZendeskTicketFilterPreviewCount
} from '../api';
import WarningMessageBody from './WarningMessageBody';
import TagModal from './TagModal';
import { useTagManager } from './useTagManager';

const FILTER_TYPE_OPTIONS = [
  { value: 'tag', label: 'Tag' },
  { value: 'organization', label: 'Organization' },
  { value: 'both', label: 'Tag & Organization' },
  { value: 'all', label: 'All Tickets' }
];

const ZendeskTicketModalManager = ({
  activeModal,
  modalProps,
  closeModal,
  setErrorIds,
  setFilterLoadingIds,
  setTicketLoadingIds,
  setIsSyncingAll
}) => {
  const { showSuccess, showError } = useNotification();
  const queryClient = useQueryClient();

  const [filterType, setFilterType] = useState(FILTER_TYPE_OPTIONS[0]);
  const [selectedTagOptions, setSelectedTagOptions] = useState([]);
  const [selectedOrgOptions, setSelectedOrgOptions] = useState([]);
  const [filterName, setFilterName] = useState('');
  const [autoFilterName, setAutoFilterName] = useState('');
  const [filterError, setFilterError] = useState('');
  const [availableTagValues, setAvailableTagValues] = useState([]);
  const [availableOrganizations, setAvailableOrganizations] = useState([]);
  const [isLoadingTags, setIsLoadingTags] = useState(false);
  const [isLoadingOrganizations, setIsLoadingOrganizations] = useState(false);
  const [previewCount, setPreviewCount] = useState(null);
  const [isLoadingPreviewCount, setIsLoadingPreviewCount] = useState(false);
  const [pendingOverwrite, setPendingOverwrite] = useState(null);

  const [isTagModalOpen, setIsTagModalOpen] = useState(false);

  const {
    tags,
    setTags,
    selectedTags,
    setSelectedTags,
    handleTagChange
  } = useTagManager({
    scope: 'generative_ai_zendesk_tickets',
    createTagFn: createZendeskTicketsTag
  });

  const needsTag = filterType.value === 'tag' || filterType.value === 'both';
  const needsOrganization = filterType.value === 'organization' || filterType.value === 'both';
  const isSelectionReady =
    filterType.value === 'all' ||
    (needsTag && !needsOrganization && selectedTagOptions.length > 0) ||
    (needsOrganization && !needsTag && selectedOrgOptions.length > 0) ||
    (needsTag && needsOrganization && selectedTagOptions.length > 0 && selectedOrgOptions.length > 0);

  useEffect(() => {
    const loadTagValues = async () => {
      if (activeModal !== 'addFilter' || !needsTag) { return; }
      setIsLoadingTags(true);
      try {
        const response = await fetchZendeskTicketAvailableTagValues();
        setAvailableTagValues(response.data ?? []);
      } catch {
        setAvailableTagValues([]);
      } finally {
        setIsLoadingTags(false);
      }
    };
    loadTagValues();
  }, [activeModal, needsTag]);

  useEffect(() => {
    const loadOrganizations = async () => {
      if (activeModal !== 'addFilter' || !needsOrganization) { return; }
      setIsLoadingOrganizations(true);
      try {
        const response = await fetchZendeskTicketAvailableOrganizations();
        setAvailableOrganizations(response.data ?? []);
      } catch {
        setAvailableOrganizations([]);
      } finally {
        setIsLoadingOrganizations(false);
      }
    };
    loadOrganizations();
  }, [activeModal, needsOrganization]);

  // Preview how many tickets currently match the selected criteria before saving.
  useEffect(() => {
    if (activeModal !== 'addFilter' || !isSelectionReady) {
      setPreviewCount(null);
      setIsLoadingPreviewCount(false);
      return;
    }

    let cancelled = false;
    setIsLoadingPreviewCount(true);

    const timeoutId = setTimeout(async () => {
      try {
        const response = await fetchZendeskTicketFilterPreviewCount({
          type: filterType.value,
          tags: needsTag ? selectedTagOptions.map(option => option.value) : undefined,
          organization_ids: needsOrganization ? selectedOrgOptions.map(option => option.value) : undefined
        });
        if (!cancelled) { setPreviewCount(response.data?.count ?? null); }
      } catch {
        if (!cancelled) { setPreviewCount(null); }
      } finally {
        if (!cancelled) { setIsLoadingPreviewCount(false); }
      }
    }, 400);

    return () => {
      cancelled = true;
      clearTimeout(timeoutId);
    };
  }, [activeModal, isSelectionReady, filterType, needsTag, needsOrganization, selectedTagOptions, selectedOrgOptions]);

  // Suggest a display name from the selection, but leave user edits untouched.
  useEffect(() => {
    if (activeModal !== 'addFilter') { return; }

    const parts = [];
    if (needsOrganization && selectedOrgOptions.length > 0) { parts.push(selectedOrgOptions.map(option => option.label).join(', ')); }
    if (needsTag && selectedTagOptions.length > 0) { parts.push(selectedTagOptions.map(option => option.label).join(', ')); }

    const suggestion = filterType.value === 'all' ? 'All Tickets' : parts.join(' & ');

    if (suggestion && (filterName === '' || filterName === autoFilterName)) {
      setFilterName(suggestion);
      setAutoFilterName(suggestion);
    }
  }, [activeModal, filterType, needsTag, needsOrganization, selectedTagOptions, selectedOrgOptions, filterName, autoFilterName]);

  const resetFilterForm = () => {
    setFilterType(FILTER_TYPE_OPTIONS[0]);
    setSelectedTagOptions([]);
    setSelectedOrgOptions([]);
    setFilterName('');
    setAutoFilterName('');
    setFilterError('');
    setPreviewCount(null);
    setPendingOverwrite(null);
  };

  const submitFilter = async (payload, force = false) => {
    try {
      await saveZendeskTicketFilter(force ? { ...payload, force: true } : payload);
      queryClient.invalidateQueries({ queryKey: ['generative-ai/zendesk/tickets'] });
      showSuccess(force ? 'Filter overwritten successfully!' : 'Ticket filter saved successfully!');
      closeModal();
      resetFilterForm();
    } catch (error) {
      const data = error.response?.data;

      if (error.response?.status === 409 && data?.error === 'duplicate') {
        setFilterError(data.message ?? 'A filter with these criteria already exists.');
        return;
      }

      if (error.response?.status === 409 && data?.error === 'name_conflict') {
        setFilterError('');
        setPendingOverwrite({ existingFilter: data.filter, payload });
        return;
      }

      if (error.response?.status === 409 && data?.error === 'name_taken') {
        setFilterError(data.message ?? 'Another filter is already using that name.');
        return;
      }

      setFilterError(
        data?.error ??
        (data?.errors
          ? Object.values(data.errors)[0][0]
          : 'Failed to save filter.')
      );
    }
  };

  const handleSaveFilter = async () => {
    if (
      (needsTag && selectedTagOptions.length === 0) ||
      (needsOrganization && selectedOrgOptions.length === 0) ||
      !filterName
    ) {
      setFilterError('Please fill in all fields before saving.');
      return;
    }
    setFilterError('');
    await submitFilter({
      type: filterType.value,
      tags: needsTag ? selectedTagOptions.map(option => option.value) : undefined,
      organization_ids: needsOrganization ? selectedOrgOptions.map(option => option.value) : undefined,
      organization_names: needsOrganization ? selectedOrgOptions.map(option => option.label) : undefined,
      name: filterName
    });
  };

  const handleConfirmOverwrite = async () => {
    if (!pendingOverwrite) { return; }
    await submitFilter(pendingOverwrite.payload, true);
  };

  const confirmationActions = {
    deleteFilter: {
      header: 'Confirm Delete Filter',
      body: (
        <WarningMessageBody
          contentHeader={`Are you sure you want to delete the filter "${modalProps.filter?.name}"?`}
          contentBody="All synced tickets under this filter will be permanently removed."
          warningText="This action cannot be undone."
        />
      ),
      confirmText: 'Delete',
      handleConfirm: async () => {
        const filterId = modalProps.filter.id;
        setFilterLoadingIds(prev => ({ ...prev, [filterId]: true }));
        try {
          await deleteZendeskTicketFilter(filterId);
          showSuccess(`Filter "${modalProps.filter.name}" delete requested`);
          queryClient.invalidateQueries({ queryKey: ['generative-ai/zendesk/tickets'] });
          closeModal();
        } catch (error) {
          showError(`Failed to delete filter: ${error.message}`);
        } finally {
          setFilterLoadingIds(prev => ({ ...prev, [filterId]: false }));
        }
      }
    },
    resyncFilter: {
      header: 'Confirm Resync Filter',
      body: (
        <WarningMessageBody
          contentHeader={`Are you sure you want to resync the filter "${modalProps.filter?.name}"?`}
          contentBody="This will refresh its matching tickets."
        />
      ),
      confirmText: 'Resync',
      handleConfirm: async () => {
        const filterId = modalProps.filter.id;
        setFilterLoadingIds(prev => ({ ...prev, [filterId]: true }));
        try {
          await resyncZendeskTicketFilter(filterId);
          showSuccess('Filter resync requested.');
          queryClient.invalidateQueries({ queryKey: ['generative-ai/zendesk/tickets', filterId] });
          closeModal();
        } catch (error) {
          showError(`Failed to resync filter: ${error.message}`);
        } finally {
          setFilterLoadingIds(prev => ({ ...prev, [filterId]: false }));
        }
      }
    },
    resyncAllFilters: {
      header: 'Confirm Resync All Filters',
      body: (
        <WarningMessageBody
          contentHeader="Are you sure you want to resync all ticket filters?"
          contentBody="This will refresh all matching tickets."
        />
      ),
      confirmText: 'Resync All',
      handleConfirm: async () => {
        setIsSyncingAll(true);
        try {
          await resyncAllZendeskTickets();
          showSuccess('Ticket filters resync requested.');
          queryClient.invalidateQueries({ queryKey: ['generative-ai/zendesk/tickets'] });
          closeModal();
        } catch (error) {
          showError(`Failed to resync filters: ${error.message}`);
        } finally {
          setIsSyncingAll(false);
        }
      }
    },
    recrawlTicket: {
      header: 'Confirm Recrawl',
      body: (
        <WarningMessageBody
          contentHeader={`Are you sure you want to recrawl the ticket "${modalProps.ticket?.subject}"?`}
          contentBody="This will update its content."
        />
      ),
      confirmText: 'Recrawl',
      handleConfirm: async () => {
        const ticketId = modalProps.ticket.id;
        const filterId = modalProps.filterId;

        setErrorIds(prev => {
          const next = { ...prev };
          delete next[ticketId];
          return next;
        });
        setTicketLoadingIds(prev => ({ ...prev, [ticketId]: true }));

        try {
          await recrawlZendeskTicket(ticketId);
          showSuccess('Ticket recrawl requested');
          queryClient.invalidateQueries({ queryKey: ['generative-ai/zendesk/tickets', filterId] });
        } catch (error) {
          if (error.response?.status >= 500) {
            setErrorIds(prev => ({ ...prev, [ticketId]: 'Failed to recrawl the ticket.' }));
          }
          showError(`Failed to recrawl ticket: ${error.message}`);
        } finally {
          setTicketLoadingIds(prev => ({ ...prev, [ticketId]: false }));
          closeModal();
        }
      }
    }
  };

  useEffect(() => {
    const openAndLoadTags = async () => {
      if (activeModal === 'tags' && modalProps?.ticket?.id) {
        try {
          const availableResp = await fetchZendeskTicketsAvailableTags();
          const availableTags = availableResp?.data?.data ?? [];
          setTags(availableTags);

          const assignedResp = await fetchZendeskTicketTags(modalProps.ticket.id);
          const tagList = assignedResp?.data?.data ?? [];
          const ids = tagList.map(t => t.id);

          setSelectedTags(ids);
          setIsTagModalOpen(true);
        } catch (error) {
          showError(error.message || 'Failed to load tags.');
          setIsTagModalOpen(true);
        }
      }
    };

    openAndLoadTags();

    return () => {
      if (activeModal !== 'tags') {
        setSelectedTags([]);
        setIsTagModalOpen(false);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeModal, modalProps?.ticket?.id]);

  if (!activeModal) {
    return null;
  }

  switch (activeModal) {
    case 'addFilter':
      if (pendingOverwrite) {
        return (
          <Modal
            header="Confirm Overwrite Filter"
            handleCancel={() => setPendingOverwrite(null)}
            handleConfirm={handleConfirmOverwrite}
            confirmText="Overwrite"
            cancelText="Cancel"
            body={
              <WarningMessageBody
                contentHeader={`A filter with these criteria already exists as "${pendingOverwrite.existingFilter?.name}".`}
                contentBody={`Overwriting will rename it to "${pendingOverwrite.payload.name}".`}
                warningText="Its already-synced tickets are kept as-is."
              />
            }
          />
        );
      }

      return (
        <Modal
          header="Add Ticket Filter"
          handleCancel={() => {
            closeModal();
            resetFilterForm();
          }}
          handleConfirm={handleSaveFilter}
          confirmText="Save"
          cancelText="Cancel"
          disableConfirmButton={!isSelectionReady || !filterName}
          body={
            <Flex col gap="sm">
              {filterError && (
                <p style={{ color: 'red' }}>{filterError}</p>
              )}
              <Flex fat col>
                <Label htmlFor="filterType">Filter By</Label>
                <Select
                  id="filterType"
                  value={filterType}
                  onChange={(option) => {
                    setFilterType(option);
                    setSelectedTagOptions([]);
                    setSelectedOrgOptions([]);
                  }}
                  options={FILTER_TYPE_OPTIONS}
                />
              </Flex>
              {needsOrganization && (
                <Flex fat col>
                  <Label htmlFor="filterOrganization">Organization</Label>
                  <Select
                    id="filterOrganization"
                    value={selectedOrgOptions[0] ?? null}
                    isLoading={isLoadingOrganizations}
                    onChange={(option) => setSelectedOrgOptions(option ? [option] : [])}
                    options={availableOrganizations.map(org => ({
                      value: String(org.id),
                      label: org.name
                    }))}
                    placeholder="Select an organization"
                  />
                </Flex>
              )}
              {needsTag && (
                <Flex fat col>
                  <Label htmlFor="filterTag">Tag</Label>
                  <Select
                    id="filterTag"
                    isMulti
                    value={selectedTagOptions}
                    isLoading={isLoadingTags}
                    onChange={(options) => setSelectedTagOptions(options ?? [])}
                    options={availableTagValues.map(tag => ({
                      value: tag.name,
                      label: `${tag.name}`
                    }))}
                    placeholder="Select one or more tags"
                  />
                </Flex>
              )}
              <Flex fat col>
                <Label htmlFor="filterCount">Estimated Tickets</Label>
                <TextInput
                  id="filterCount"
                  disabled
                  value={
                    !isSelectionReady
                      ? ''
                      : isLoadingPreviewCount
                        ? 'Loading…'
                        : (previewCount === null ? 'Unavailable' : String(previewCount))
                  }
                  placeholder="Select filter criteria to see ticket count"
                />
                <Label gray>The estimation is ONLY for tickets that matched all information.</Label>
              </Flex>
              <Flex fat col>
                <Label htmlFor="filterName">Display Name</Label>
                <TextInput
                  id="filterName"
                  value={filterName}
                  onChange={e => setFilterName(e.target.value)}
                  placeholder="e.g. VIP Customers"
                />
              </Flex>
            </Flex>
          }
        />
      );

    case 'tags':
      return (
        <TagModal
          isVisible={isTagModalOpen}
          header="Zendesk Ticket Tags"
          onCancel={() => {
            setIsTagModalOpen(false);
            setSelectedTags([]);
            closeModal();
          }}
          onConfirm={async () => {
            try {
              await saveZendeskTicketTags({
                ticketId: modalProps.ticket.id,
                groupIds: selectedTags
              });

              queryClient.invalidateQueries({ queryKey: ['generative-ai/zendesk/tickets', modalProps.filterId] });

              setIsTagModalOpen(false);
              setSelectedTags([]);
              showSuccess('Tags assigned successfully!');
              closeModal();
            } catch (error) {
              showError(error.message || 'Failed to assign tags.');
            }
          }}
          tags={tags}
          selectedTags={selectedTags}
          handleTagChange={handleTagChange}
        />
      );

    case 'confirmation': {
      const config = confirmationActions[modalProps.type];
      return (
        <Modal
          header={config.header}
          body={config.body}
          cancelText="Cancel"
          confirmText={config.confirmText}
          handleCancel={closeModal}
          handleConfirm={config.handleConfirm}
        />
      );
    }

    default:
      return null;
  }
};

ZendeskTicketModalManager.propTypes = {
  activeModal: PropTypes.string,
  modalProps: PropTypes.object,
  closeModal: PropTypes.func.isRequired,
  setErrorIds: PropTypes.func.isRequired,
  setFilterLoadingIds: PropTypes.func.isRequired,
  setTicketLoadingIds: PropTypes.func.isRequired,
  setIsSyncingAll: PropTypes.func.isRequired
};

export default ZendeskTicketModalManager;
